import { formatReferencesMarkdown } from '../core/parsers/markdownParser'
import { BotResponse, ChatMessageSchema, ChatSchema, MessageThread, Reference, Source } from '../types/chatTypes'

/**
 * Maps backend Source[] to frontend Reference[]
 */
const mapSourcesToReferences = (sources?: Source[]): Reference[] => {
  if (!sources) return []

  return sources.map(source => ({
    title: source.title,
    url: source.url,
    page: source.page,
    video_url: source.video_url,
  }))
}

export const parseChatToMessageThread = (chat: ChatSchema | null): MessageThread[] => {
  if (!chat || !chat.messages || chat.messages.length === 0) {
    return []
  }

  const messageThreads: MessageThread[] = []
  let index = 0

  while (index < chat.messages.length) {
    const msg = chat.messages[index]

    const isUser = msg.is_user

    if (isUser) {
      // Collect all consecutive bot messages after this user message
      const botMessages: ChatMessageSchema[] = []
      let nextIndex = index + 1

      while (nextIndex < chat.messages.length) {
        const nextMsg = chat.messages[nextIndex]
        const nextIsUser = nextMsg.is_user
        if (nextIsUser) break
        botMessages.push(nextMsg)
        nextIndex++
      }

      // Transform bot messages into answer objects for the UI
      const answers: BotResponse[] =
        botMessages.length > 0
          ? botMessages.map(botMessage => {
              // Format references if they exist

              // Map backend Source[] to frontend Reference[]
              const references = mapSourcesToReferences(botMessage?.sources)


              const knowledgeSource = botMessage.knowledge_source || 'LLM_ONLY'
              const isInternal = knowledgeSource === 'INTERNAL_KB'
              const hasMultipleAnswers = botMessages.length > 1

              const answer: BotResponse = {
                message_id: botMessage.id,
                answer: botMessage?.content || '',
                is_sensitive: botMessage.is_sensitive,
                isStreaming: false,
                needsFeedback: true,
                knowledgeSource,
                references,
                disclaimers: botMessage.disclaimers,
                // Internal knowledge is primary (expanded), others are minimized
                isPrimary: isInternal,
                isMinimized: !isInternal && hasMultipleAnswers,
              }

              if (botMessage.content_validation_result) {
                answer.error = botMessage.content_validation_result
                const violations = botMessage.content_validation_details?.violations || []
                if (violations.length > 0) {
                  answer.errorDetails = { policy_violations: violations }
                }
              }

              return answer
            })
          : [
              {
                // If there's no bot response yet, provide empty answer
                message_id: '',
                answer: '',
                is_sensitive: false,
                isStreaming: false,
                needsFeedback: true,
                knowledgeSource: 'LLM_ONLY' as const,
                isPrimary: true,
                isMinimized: false,
              },
            ]

      // Sort answers so internal knowledge comes last (will be displayed as primary)
      answers.sort((a, b) => {
        if (a.knowledgeSource === 'INTERNAL_KB' && b.knowledgeSource !== 'INTERNAL_KB') return 1
        if (a.knowledgeSource !== 'INTERNAL_KB' && b.knowledgeSource === 'INTERNAL_KB') return -1
        return 0
      })

      const thread: MessageThread = {
        tempId: msg.id,
        question: msg.content,
        attachedAsset: msg.documents?.[0],
        answers,
      }

      if (msg.content_validation_result) {
        thread.inputValidationError = new Error(msg.content_validation_result)
        const violations = msg.content_validation_details?.violations || []
        if (violations.length > 0) {
          thread.inputValidationErrorDetails = { policy_violations: violations }
        }
        // Clear any answers when input was invalid
        thread.answers = []
      }

      messageThreads.push(thread)

      // Skip all processed messages (user + all bot responses)
      index = nextIndex
    } else {
      // If the current message is a bot message without a preceding user message
      // (this can happen in some scenarios), create a thread with just the bot message
      const thread: MessageThread = {
        tempId: msg.id,
        question: undefined,
        answers: [
          (() => {
            // Map backend Source[] to frontend Reference[]
            const references = mapSourcesToReferences(msg.sources)

            const base: BotResponse = {
              message_id: msg.id,
              answer: msg.content || '',
              is_sensitive: msg.is_sensitive,
              isStreaming: false,
              needsFeedback: true,
              knowledgeSource: msg.knowledge_source || 'LLM_ONLY',
              references,
              disclaimers: msg.disclaimers,
              isPrimary: true,
              isMinimized: false,
            }

            if (msg.content_validation_result) {
              base.error = msg.content_validation_result
              const violations = msg.content_validation_details?.violations || []
              if (violations.length > 0) {
                base.errorDetails = { policy_violations: violations }
              }
            }

            return base
          })(),
        ],
      }

      messageThreads.push(thread)
      index += 1 // Move to the next message
    }
  }

  return messageThreads.filter(Boolean)
}
